import {
  Badge,
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { brandPalette } from "../../../constants/brandPalette";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RiFileExcel2Line } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import ReactSelect from "react-select";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import PdfPreviewModal, {
  liveClassworkPdfUrl,
} from "../../../components/student/PdfPreviewModal";
import {
  getLiveVideosRequest,
  livevideoReportRequest,
  updateStudentLiveVideoRecordRequest,
} from "../../../modules/admin-modules/liveVideos/Actions";
import { centerHwRequest } from "../../../modules/admin-modules/reports/Actions";

const CAMERA_OPTIONS = [
  { value: 1, label: "✅" },
  { value: 0, label: "❌" },
];

const CAMERA_NA_OPTION = { value: 2, label: "NA" };
const ALL_CAMERA_OPTIONS = [...CAMERA_OPTIONS, CAMERA_NA_OPTION];

function cameraOptionsForRow(isCenter) {
  return isCenter ? ALL_CAMERA_OPTIONS : CAMERA_OPTIONS;
}

const CLASS_WORK_OPTIONS = [
  { value: 3, label: "Great job" },
  { value: 2, label: "Good" },
  { value: 1, label: "Incomplete" },
  { value: 0, label: "Not submitted" },
];

const CLASS_WORK_LABELS = {
  0: "Not submitted",
  1: "Incomplete",
  2: "Good",
  3: "Great job",
};

const DEFAULT_ROW = {
  camera: 0,
  grade: 0,
  customMessage: "",
};

function parseCamera(value) {
  if (value === 2 || value === "2" || String(value).toLowerCase() === "na") return 2;
  if (value === 1 || value === "1" || value === "on" || value === true) return 1;
  if (value === 0 || value === "0" || value === "off" || value === false) return 0;
  return 0;
}

function parseClassworkStatus(value) {
  if (value == null || value === "") return 0;
  const n = Number(value);
  if (n === 0 || n === 1 || n === 2 || n === 3) return n;
  const key = String(value).trim().toLowerCase();
  if (key === "not submitted") return 0;
  if (key === "incomplete") return 1;
  if (key === "good") return 2;
  if (key === "great job" || key === "greatjob") return 3;
  return 0;
}

function persistedRowFields(row) {
  const pivot = row?.pivot || {};
  return {
    camera: parseCamera(row?.camera ?? pivot.camera),
    grade: parseClassworkStatus(row?.classwork_status ?? pivot.classwork_status),
    customMessage: row?.notes ?? pivot.notes ?? "",
  };
}

function getRowFields(rowState, studentId, row) {
  return {
    ...DEFAULT_ROW,
    ...persistedRowFields(row),
    ...(rowState[studentId] || {}),
  };
}

function cameraLabel(camera) {
  const value = parseCamera(camera);
  if (value === 1) return "On";
  if (value === 0) return "Off";
  if (value === 2) return "NA";
  return "—";
}

function classworkLabel(status) {
  return CLASS_WORK_LABELS[parseClassworkStatus(status)] || "—";
}

function isCenterStudent(row) {
  const value = row?.pivot?.is_center ?? row?.student?.pivot?.is_center ?? row?.is_center;
  return value === 1 || value === true || value === "1";
}

function formatJoinedAt(isoString) {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return String(isoString);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(isoString);
  }
}

function sanitizeFilePart(name) {
  const cleaned = String(name || "live-session")
    .replace(/[\\/:*?"<>|]+/g, " ")
    .trim()
    .replace(/\s+/g, "-");
  return (cleaned || "live-session").slice(0, 80);
}

function styleExcelHeader(row) {
  row.font = { bold: true, color: { argb: "FFFFFFFF" } };
  row.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF5B0D16" },
  };
  row.alignment = { vertical: "middle", wrapText: true };
  row.height = 22;
}

function buildAttendanceExportRows(students, rowState) {
  return students
    .map((row, index) => {
      const id = row.student?.id ?? row.id;
      if (id == null) return null;
      const st = getRowFields(rowState, id, row);
      const classworkFile = row.classwork ? String(row.classwork) : "";
      const joinedAt = row.pivot?.created_at;
      return {
        index: index + 1,
        studentId: id,
        name: row.student?.name ?? row.name ?? "—",
        center: isCenterStudent(row) ? "Yes" : "No",
        code: row.user_name ?? row.code ?? row.student?.user_name ?? "—",
        phone: String(row.phone ?? row.student?.phone ?? "—"),
        parentPhone: String(row.parent_phone ?? row.student?.parent_phone ?? "—"),
        group: row.group?.name ?? row.student?.group?.name ?? "—",
        joinedAt: joinedAt ? formatJoinedAt(joinedAt) : "—",
        classworkPdf: classworkFile ? "Yes" : "No",
        classworkFile: classworkFile || "—",
        classworkUrl: classworkFile ? liveClassworkPdfUrl(classworkFile) : "—",
        camera: cameraLabel(st.camera),
        classWork: classworkLabel(st.grade),
        notes: String(st.customMessage ?? "").trim() || "—",
      };
    })
    .filter(Boolean);
}

function buildParentMessage({
  subjectName,
  sessionTitle,
  studentName,
  camera,
  grade,
  joinedAt,
  customMessage,
}) {
  const subj = subjectName?.trim() || "your subject";
  const session = sessionTitle?.trim() || "Live session";
  const student = studentName?.trim() || "Student";
  const extra = customMessage?.trim();
  const joinedLine =
    joinedAt != null && String(joinedAt).trim()
      ? `Student joined the live at ${formatJoinedAt(joinedAt)}`
      : "";

  const lines = [
    `Edutopia — ${subj}`,
    `Live session attendance update`,
    `Session: ${session}`,
    `Student: ${student}`,
  ];
  if (parseCamera(camera) !== 2) {
    lines.push(`Camera: ${cameraLabel(camera)}`);
  }
  lines.push(`Class work evaluation: ${classworkLabel(grade)}`);
  if (extra) lines.push(extra);
  lines.push(`Thank you.`);
  if (joinedLine) lines.push(joinedLine);

  return lines.join("\n");
}

const focusRing = "0 0 0 1px rgba(91, 13, 22, 0.22)";

const liveSelectBase = adminSelectStyles({ menuZIndex: 1600 });

const selectStyles = {
  ...liveSelectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  menuList: (styles) => ({
    ...liveSelectBase.menuList(styles),
    padding: "4px",
    overflowX: "hidden",
    overflowY: "auto",
  }),
  control: (styles, state) => ({
    ...liveSelectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
  }),
};

/** Compact row selects — theme-aware for dark mode */
const inlineSelectStyles = {
  ...liveSelectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...liveSelectBase.control(styles, state),
    minHeight: "38px",
    minWidth: "120px",
    fontSize: "14px",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    opacity: state.isDisabled ? 0.55 : 1,
  }),
  valueContainer: (styles) => ({ ...styles, padding: "0 8px" }),
  menuList: (styles) => ({
    ...liveSelectBase.menuList(styles),
    padding: "4px",
    overflowX: "hidden",
    overflowY: "auto",
  }),
  option: (styles, opts) => ({
    ...liveSelectBase.option(styles, opts),
    borderRadius: "8px",
    margin: "2px 0",
    padding: "8px 10px",
    fontSize: "14px",
  }),
  placeholder: (styles) => ({
    ...liveSelectBase.placeholder(styles),
    fontSize: "13px",
  }),
  dropdownIndicator: (styles, state) => ({
    ...styles,
    padding: "4px",
    color: state.isFocused ? brandPalette.primary : "var(--text-muted)",
  }),
};

const cameraSelectStyles = {
  ...inlineSelectStyles,
  control: (styles, state) => ({
    ...inlineSelectStyles.control(styles, state),
    minWidth: "88px",
  }),
  singleValue: (styles, state) => ({
    ...liveSelectBase.singleValue(styles, state),
    fontSize: "18px",
    lineHeight: 1,
    textAlign: "center",
  }),
  option: (styles, opts) => ({
    ...inlineSelectStyles.option(styles, opts),
    fontSize: "18px",
    textAlign: "center",
    lineHeight: 1.2,
  }),
};

const LiveVideoAttendanceReport = () => {
  const dispatch = useDispatch();
  const user = useSelector((s) => s.user.userData);
  const subjectName = user?.subject?.name || "";

  const liveVideos = useSelector((s) => s.adminLiveVideos.liveVideos);
  const liveVideosReport = useSelector((s) => s.adminLiveVideos.liveVideosReport);
  const liveListLoading = useSelector((s) => s.adminLiveVideos.isLoading);
  const reportSending = useSelector((s) => s.reports.isLoading);

  const [liveVideoId, setLiveVideoId] = useState("");
  const [rowState, setRowState] = useState({});
  const [classworkPreview, setClassworkPreview] = useState(null);

  useEffect(() => {
    dispatch(getLiveVideosRequest());
  }, [dispatch]);

  useEffect(() => {
    setRowState({});
    if (!liveVideoId) return;
    dispatch(livevideoReportRequest(liveVideoId));
  }, [liveVideoId, dispatch]);

  const liveOptions = useMemo(() => {
    const raw = Array.isArray(liveVideos) ? liveVideos : [];
    return raw.map((v) => ({
      value: String(v.id),
      label: v.title || `Live #${v.id}`,
    }));
  }, [liveVideos]);

  const selectedLive = useMemo(() => {
    const raw = Array.isArray(liveVideos) ? liveVideos : [];
    return raw.find((v) => String(v.id) === String(liveVideoId)) || null;
  }, [liveVideos, liveVideoId]);

  const sessionTitle = selectedLive?.title || "";

  const sessionMeta = useMemo(() => {
    const report =
      liveVideosReport && !Array.isArray(liveVideosReport) ? liveVideosReport : null;
    return {
      id: report?.id ?? selectedLive?.id ?? liveVideoId,
      title: report?.title ?? selectedLive?.title ?? sessionTitle,
      startTime: report?.start_time ?? selectedLive?.start_time ?? "",
      zoomLink: report?.zoom_link ?? selectedLive?.zoom_link ?? "",
      active: report?.active ?? selectedLive?.active,
      chapterId: report?.chapter_id ?? selectedLive?.chapter_id,
      subjectId: report?.subject_id ?? selectedLive?.subject_id,
    };
  }, [liveVideosReport, selectedLive, liveVideoId, sessionTitle]);

  const students = useMemo(() => {
    if (liveVideosReport == null) return [];

    if (Array.isArray(liveVideosReport)) {
      const video = liveVideosReport.find((v) => String(v?.id) === String(liveVideoId));
      const list = video?.students ?? [];
      return Array.isArray(list) ? list : [];
    }

    const list = liveVideosReport.students ?? liveVideosReport;
    return Array.isArray(list) ? list : [];
  }, [liveVideosReport, liveVideoId]);

  const updateRowField = useCallback((studentId, field, value) => {
    setRowState((prev) => ({
      ...prev,
      [studentId]: { ...(prev[studentId] || {}), [field]: value },
    }));
  }, []);

  const sendRowToParent = useCallback(
    (studentId, studentName, joinedAtIso) => {
      const row = students.find((item) => String(item.student?.id ?? item.id) === String(studentId));
      const st = getRowFields(rowState, studentId, row);
      if (st.camera == null || st.grade == null) return;
      dispatch(
        updateStudentLiveVideoRecordRequest({
          live_video_id: liveVideoId,
          student_id: studentId,
          camera: parseCamera(st.camera),
          classwork_status: parseClassworkStatus(st.grade),
          notes: st.customMessage ?? "",
        })
      );
      const evaluation = buildParentMessage({
        subjectName,
        sessionTitle,
        studentName,
        camera: st.camera,
        grade: st.grade,
        joinedAt: joinedAtIso,
        customMessage: st.customMessage,
      });
      dispatch(
        centerHwRequest({
          student_id: studentId,
          evaluation,
        })
      );
    },
    [dispatch, subjectName, sessionTitle, rowState, students, liveVideoId]
  );

  const exportToExcel = useCallback(async () => {
    if (!students.length) return;

    const rows = buildAttendanceExportRows(students, rowState);
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "Edutopia";
    workbook.created = new Date();

    const summary = workbook.addWorksheet("Summary");
    summary.columns = [
      { header: "Field", key: "field", width: 28 },
      { header: "Value", key: "value", width: 60 },
    ];
    styleExcelHeader(summary.getRow(1));

    const cameraCounts = { On: 0, Off: 0, NA: 0 };
    const classWorkCounts = {
      "Great job": 0,
      Good: 0,
      Incomplete: 0,
      "Not submitted": 0,
    };
    let centerCount = 0;
    let classworkPdfCount = 0;
    rows.forEach((row) => {
      if (row.center === "Yes") centerCount += 1;
      if (row.classworkPdf === "Yes") classworkPdfCount += 1;
      if (cameraCounts[row.camera] != null) cameraCounts[row.camera] += 1;
      if (classWorkCounts[row.classWork] != null) classWorkCounts[row.classWork] += 1;
    });

    const activeLabel =
      sessionMeta.active === 1 || sessionMeta.active === true
        ? "Yes"
        : sessionMeta.active === 0 || sessionMeta.active === false
          ? "No"
          : sessionMeta.active ?? "—";

    [
      ["Report", "Live video attendance"],
      ["Subject", subjectName || "—"],
      ["Session", sessionMeta.title || "—"],
      ["Session ID", sessionMeta.id ?? "—"],
      ["Start time", sessionMeta.startTime || "—"],
      ["Zoom link", sessionMeta.zoomLink || "—"],
      ["Active", activeLabel],
      ["Chapter ID", sessionMeta.chapterId ?? "—"],
      ["Subject ID", sessionMeta.subjectId ?? "—"],
      ["Exported at", new Date().toLocaleString()],
      ["Total attendees", rows.length],
      ["Center students", centerCount],
      ["Online students", rows.length - centerCount],
      ["Camera on", cameraCounts.On],
      ["Camera off", cameraCounts.Off],
      ["Camera NA", cameraCounts.NA],
      ["Class work — Great job", classWorkCounts["Great job"]],
      ["Class work — Good", classWorkCounts.Good],
      ["Class work — Incomplete", classWorkCounts.Incomplete],
      ["Class work — Not submitted", classWorkCounts["Not submitted"]],
      ["Classwork PDF submitted", classworkPdfCount],
    ].forEach(([field, value]) => {
      summary.addRow({ field, value: value == null || value === "" ? "—" : value });
    });

    const attendees = workbook.addWorksheet("Attendees");
    attendees.columns = [
      { header: "#", key: "index", width: 8 },
      { header: "Student ID", key: "studentId", width: 12 },
      { header: "Student", key: "name", width: 28 },
      { header: "Center", key: "center", width: 10 },
      { header: "Code", key: "code", width: 14 },
      { header: "Phone", key: "phone", width: 18 },
      { header: "Parent phone", key: "parentPhone", width: 18 },
      { header: "Group", key: "group", width: 18 },
      { header: "Joined live at", key: "joinedAt", width: 24 },
      { header: "Classwork PDF", key: "classworkPdf", width: 16 },
      { header: "Classwork file", key: "classworkFile", width: 22 },
      { header: "Classwork URL", key: "classworkUrl", width: 42 },
      { header: "Camera", key: "camera", width: 12 },
      { header: "Class work", key: "classWork", width: 16 },
      { header: "Notes", key: "notes", width: 40 },
    ];
    styleExcelHeader(attendees.getRow(1));
    attendees.views = [{ state: "frozen", ySplit: 1 }];
    attendees.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: attendees.columns.length },
    };

    rows.forEach((row) => {
      const added = attendees.addRow(row);
      added.getCell("code").numFmt = "@";
      added.getCell("phone").numFmt = "@";
      added.getCell("parentPhone").numFmt = "@";
      added.getCell("notes").alignment = { wrapText: true, vertical: "top" };
      if (row.classworkUrl && row.classworkUrl !== "—") {
        added.getCell("classworkUrl").value = {
          text: row.classworkUrl,
          hyperlink: row.classworkUrl,
        };
        added.getCell("classworkUrl").font = { color: { argb: "FF0563C1" }, underline: true };
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(
      blob,
      `live-video-attendance-${sanitizeFilePart(sessionMeta.title)}-${
        new Date().toISOString().split("T")[0]
      }.xlsx`
    );
  }, [students, rowState, sessionMeta, subjectName]);

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
      >
        <Flex
          w="100%"
          mb={5}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "flex-start" }}
          gap={4}
          flexWrap="wrap"
        >
          <Box flex="1" minW="0">
            <Text fontSize={{ base: "18px", md: "24px" }} fontWeight="900" color="var(--text-primary)">
              Live video attendance report
            </Text>
            <Text mt={1} fontSize="sm" fontWeight="700" color="var(--text-muted)">
              Select a live session, choose camera status and class-work evaluation for each student,
              then click Send to notify the parent (same channel as center homework notifications).
              Optional notes in each row are included only for that student&apos;s message.
            </Text>
          </Box>
          <Flex align="flex-start" gap={3} flexWrap="wrap" justifyContent={{ base: "flex-start", md: "flex-end" }}>
            <Box minW={{ base: "100%", md: "320px" }} maxW={{ base: "100%", md: "420px" }}>
              <ReactSelect
                options={liveOptions}
                isLoading={liveListLoading}
                placeholder="Select live video"
                styles={selectStyles}
                value={liveOptions.find((o) => o.value === String(liveVideoId)) || null}
                onChange={(opt) => setLiveVideoId(opt?.value ? String(opt.value) : "")}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
              />
            </Box>
            {students.length > 0 ? (
              <Button
                onClick={exportToExcel}
                leftIcon={<RiFileExcel2Line />}
                bg={brandPalette.primary}
                color={brandPalette.cream}
                fontWeight="800"
                borderRadius="full"
                px={6}
                _hover={{ bg: brandPalette.accentDark }}
              >
                Export to Excel
              </Button>
            ) : null}
            <LogoutMenu />
          </Flex>
        </Flex>

        <Box
          border="1px solid"
          borderColor="var(--border-subtle)"
          bg="var(--surface-raised)"
          borderRadius="18px"
          p={{ base: 4, md: 6 }}
          boxShadow="0 18px 55px rgba(0,0,0,0.12)"
          overflowX="auto"
          color="var(--text-primary)"
        >
          {!liveVideoId ? (
            <Text fontWeight="600" color="var(--text-muted)">
              Choose a live video to load attendees.
            </Text>
          ) : liveListLoading && students.length === 0 ? (
            <Text fontWeight="600" color="var(--text-primary)">
              Loading attendees…
            </Text>
          ) : students.length === 0 ? (
            <Text fontWeight="600" color="var(--text-muted)">
              No students returned for this session.
            </Text>
          ) : (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th color="var(--text-primary)">#</Th>
                  <Th color="var(--text-primary)">Student</Th>
                  <Th color="var(--text-primary)">Code</Th>
                  <Th color="var(--text-primary)">Phone</Th>
                  <Th color="var(--text-primary)">Parent phone</Th>
                  <Th color="var(--text-primary)">Joined live at</Th>
                  <Th color="var(--text-primary)">Classwork PDF</Th>
                  <Th color="var(--text-primary)">Camera</Th>
                  <Th color="var(--text-primary)">Class work</Th>
                  <Th color="var(--text-primary)" minW="200px">
                    Optional note
                  </Th>
                  <Th color="var(--text-primary)">Send</Th>
                </Tr>
              </Thead>
              <Tbody>
                {students.map((row, index) => {
                  const id = row.student?.id ?? row.id;
                  if (id == null) return null;
                  const st = getRowFields(rowState, id, row);
                  const name = row.student?.name ?? row.name ?? "—";
                  const code = row.user_name ?? row.code ?? "—";
                  const joinedAt = row.pivot?.created_at;
                  const joinedDisplay = joinedAt ? formatJoinedAt(joinedAt) : "—";
                  const canSend = st.camera != null && st.grade != null;
                  const isCenter = isCenterStudent(row);
                  return (
                    <Tr key={id}>
                      <Td fontWeight="700">#{index + 1}</Td>
                      <Td>
                        <Flex align="center" gap={2} flexWrap="wrap">
                          <Text fontWeight="600">{name}</Text>
                          {isCenter ? (
                            <Badge
                              px={2}
                              py={0.5}
                              borderRadius="md"
                              fontWeight="800"
                              fontSize="9px"
                              letterSpacing="0.08em"
                              textTransform="uppercase"
                              bg="rgba(91, 13, 22, 0.12)"
                              color={brandPalette.primary}
                            >
                              Center
                            </Badge>
                          ) : null}
                        </Flex>
                      </Td>
                      <Td>{code}</Td>
                      <Td>{row.phone || "—"}</Td>
                      <Td>{row.parent_phone || "—"}</Td>
                      <Td fontSize="sm" whiteSpace="nowrap">
                        {joinedDisplay}
                      </Td>
                      <Td verticalAlign="middle">
                        {row.classwork ? (
                          <Button
                            size="sm"
                            variant="outline"
                            borderColor="var(--border-subtle)"
                            color={brandPalette.primary}
                            fontWeight="800"
                            borderRadius="lg"
                            onClick={() =>
                              setClassworkPreview({
                                url: liveClassworkPdfUrl(row.classwork),
                                title: `${name} — classwork`,
                              })
                            }
                          >
                            Preview
                          </Button>
                        ) : (
                          <Text fontSize="sm" color="var(--text-muted)" fontWeight="600">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td minW="148px" verticalAlign="middle">
                        <ReactSelect
                          instanceId={`cam-${id}`}
                          inputId={`cam-${id}`}
                          options={cameraOptionsForRow(isCenter)}
                          styles={cameraSelectStyles}
                          placeholder="Camera"
                          isClearable
                          isSearchable={false}
                          isDisabled={reportSending}
                          value={
                            ALL_CAMERA_OPTIONS.find((o) => o.value === parseCamera(st.camera)) ?? null
                          }
                          onChange={(opt) =>
                            updateRowField(
                              id,
                              "camera",
                              opt?.value === 0 || opt?.value === 1 || opt?.value === 2 ? opt.value : 0
                            )
                          }
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                        />
                      </Td>
                      <Td minW="168px" verticalAlign="middle">
                        <ReactSelect
                          instanceId={`grade-${id}`}
                          inputId={`grade-${id}`}
                          options={CLASS_WORK_OPTIONS}
                          styles={inlineSelectStyles}
                          placeholder="Evaluation"
                          isClearable
                          isSearchable={false}
                          isDisabled={reportSending}
                          value={
                            CLASS_WORK_OPTIONS.find((o) => o.value === parseClassworkStatus(st.grade)) ?? null
                          }
                          onChange={(opt) =>
                            updateRowField(id, "grade", opt?.value == null ? 0 : Number(opt.value))
                          }
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                        />
                      </Td>
                      <Td minW="200px" maxW="280px" verticalAlign="middle">
                        <Textarea
                          value={st.customMessage ?? ""}
                          onChange={(e) => updateRowField(id, "customMessage", e.target.value)}
                          placeholder="Optional…"
                          size="sm"
                          rows={2}
                          resize="vertical"
                          borderRadius="12px"
                          borderColor="var(--border-subtle)"
                          _focus={{ borderColor: brandPalette.primary, boxShadow: focusRing }}
                          fontWeight="600"
                          color="var(--text-primary)"
                          bg="var(--surface-muted)"
                          isDisabled={reportSending}
                        />
                      </Td>
                      <Td>
                        <Button
                          size="sm"
                          borderRadius="lg"
                          bg={brandPalette.secondary}
                          color="white"
                          fontWeight="800"
                          isDisabled={!canSend || reportSending}
                          _hover={{ bg: brandPalette.primary }}
                          onClick={() => sendRowToParent(id, name, joinedAt)}
                        >
                          Send
                        </Button>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )}
        </Box>

        <PdfPreviewModal
          isOpen={Boolean(classworkPreview?.url)}
          onClose={() => setClassworkPreview(null)}
          pdfUrl={classworkPreview?.url || ""}
          title={classworkPreview?.title || "Classwork"}
          backLabel="Back"
        />
      </Box>
    </Layout>
  );
};

export default LiveVideoAttendanceReport;
